import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";

import CircularProgress from "@material-ui/core/CircularProgress";
import withStyles from "@material-ui/core/styles/withStyles";

import Button from "~/material-dashboard/components/CustomButtons/Button.jsx";
import GridContainer from "~/material-dashboard/components/Grid/GridContainerWithSpacing.jsx";
import GridItem from "~/material-dashboard/components/Grid/GridItemWithSpacing.jsx";

import {
  CompoundedStateNotSupportedModal,
  ConfirmAddressModal,
  CouldntVerifyAddressModal,
  MissingAddressComponentsModal,
  OrderCosts,
  POBoxShippingNotSupportedModal,
  SelectedSleepMeds,
  ShippingInputs,
  ShippingRadios,
  SleepOrderCosts,
  SleepSubscriptionSelectionSection,
  SocialProof,
  StateNotSupportedModal,
  WhatHappensNext,
} from "./paymentComponents.jsx";
import {
  orderDetailsFormValidationSchema,
  orderTotalPriceWithCouponDiscount,
} from "./paymentHelpers.js";
import { googleValidateAddress } from "~/shared/addressValidation";

import { CheckoutProgress } from "../shared.jsx";

// Utils & Widgets
import {
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  SHIPPING_METHOD_TWO_DAY,
} from "~/shared/sharedConstants";
import {
  getGoogleAPIKey,
  isCompoundedOrderType,
  isConsultationTypeInsomnia,
  isMailOrderType,
  isPharmacyPickupFromVisit,
  isPharmacyPickupOrderType,
  isSleepOrderType,
  supportedUSStates,
  supportedUSStatesForCompoundedMeds,
} from "~/shared/utils/sharedUtils";
import { usStatesList } from "~/shared/widgets/autocompleteUSStates.jsx";
import {
  getLatestAddress,
  getOrderDetailsData,
  getOrderItemsList,
  getShipmentMethod,
  getVisitDifferentPharmacy,
  getVisitDifferentPharmacyJSON,
  getVisitPhone,
  getVisitProduct,
  setConsultationTypeOrderItem,
  setOrderDetailsData,
  setShipmentMethod,
  setVisitProduct,
} from "~/visits/utils/visitCookies.js";

import { kickAmaOrange, kickNavy } from "~/shared/styles/baseStyles.js";
import consultationStyles from "~/visits/styles/consultationStyles.js";

const latestAddress = getLatestAddress();

let initialFormStateValues = {
  address1: "",
  address2: "",
  city: "",
  zip: "",
  usState: "",
  phone: getVisitPhone(),
  smsConsent: true,
};

function getDefaultSleepOrderType(checkoutSessionID) {
  const currentProduct = getVisitProduct(checkoutSessionID);
  if (!isSleepOrderType(currentProduct)) {
    return isPharmacyPickupFromVisit(
      getVisitDifferentPharmacy(checkoutSessionID)
    )
      ? ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY
      : ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY;
  } else {
    return currentProduct;
  }
}

const OrderDetails = (props) => {
  const { classes } = props;
  const { consultationType } = props.match.params;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");
  if (!checkoutSessionID) {
    window.location.href = "/me/portal";
  }

  const history = useHistory();

  const googleAPIKey = getGoogleAPIKey();
  const savedOrderDetailsData = getOrderDetailsData(checkoutSessionID);

  // use saved order details if they exist (from going 'back' from payment details page)
  if (!isEmpty(savedOrderDetailsData)) {
    initialFormStateValues = {
      ...initialFormStateValues,
      ...savedOrderDetailsData,
      usState: usStatesList.find(
        (v) => v.abbreviation == savedOrderDetailsData.usState
      ),
    };
  } else if (!isEmpty(latestAddress)) {
    // otherwise try to use latest address (like in refills)
    initialFormStateValues = {
      ...initialFormStateValues,
      ...latestAddress,
      usState: usStatesList.find(
        (v) => v.abbreviation == latestAddress.usState
      ),
    };
  }

  // state variables
  const [sleepProductType, setSleepProductType] = useState(
    getDefaultSleepOrderType(checkoutSessionID)
  );
  const [showAddressModal, setShowAddressModal] = useState({});
  const [shipMethod, setShipMethod] = useState(
    getShipmentMethod(checkoutSessionID)
  );
  const [totalPrice, setTotalPrice] = useState(
    orderTotalPriceWithCouponDiscount(true, checkoutSessionID)
  );
  const [
    { address1, address2, city, usState, zip, phone, smsConsent },
    setFormState,
  ] = useState(initialFormStateValues);
  const [formIsSubmitting, setFormIsSubmitting] = useState(false);

  useEffect(() => {
    // make sure correct cookie "product" is set for sleep visits
    if (isConsultationTypeInsomnia(consultationType)) {
      setVisitProduct(sleepProductType, checkoutSessionID);
      setTotalPrice(orderTotalPriceWithCouponDiscount(true, checkoutSessionID));
      setConsultationTypeOrderItem(
        consultationType,
        sleepProductType,
        checkoutSessionID
      );
    }

    // set a default shipment method
    if (!getShipmentMethod(checkoutSessionID)) {
      setShipmentMethod("standard", checkoutSessionID);
    }

    if (typeof window.Beacon == "function") {
      // window.Beacon("init", "e67f20b1-8927-4c18-b2a9-3e8119a361ad");
    }
  }, []);

  const setSleepVisitProductFromRadio = (event) => {
    const productType = event.target.value;
    setVisitProduct(productType, checkoutSessionID);
    setSleepProductType(productType);
    setTotalPrice(orderTotalPriceWithCouponDiscount(true, checkoutSessionID));
    console.log(
      "differentPharmacyJSON: ",
      getVisitDifferentPharmacyJSON(checkoutSessionID)
    );
    console.log("getVisitProduct: ", getVisitProduct(checkoutSessionID));

    setConsultationTypeOrderItem(
      consultationType,
      productType,
      checkoutSessionID
    );
  };

  const isPOBox = (address) => {
    const poBoxRegex = /(P\.?O\.?\s*Box|Post\s*Office\s*Box|PO\s*Box)/gi;
    return (
      poBoxRegex.test(address.address1) || poBoxRegex.test(address.address2)
    );
  };

  const validateAddress = async (apiKey, address) => {
    const fixAddressModal = await googleValidateAddress(apiKey, address);
    setFormIsSubmitting(false);
    if (fixAddressModal) {
      const { modalName, modalData } = fixAddressModal;
      setShowAddressModal({ modalName, modalData });
      setFormState(address);
    } else {
      finishOrderDetailsSection(address);
    }
  };

  const finishOrderDetailsSection = (orderDetails) => {
    setShowAddressModal({}); // clear address modal
    setFormState(orderDetails);
    setFormIsSubmitting(false);

    // Check for P.O. box with two-day shipping
    if (isPOBox(orderDetails) && shipMethod === SHIPPING_METHOD_TWO_DAY) {
      setFormIsSubmitting(false);
      setShowAddressModal({
        modalName: "poBoxShippingNotSupported",
      });
      return;
    }

    // double check that google address validation didn't set an unsupported state
    if (
      isMailOrderType(getVisitProduct(checkoutSessionID)) &&
      !supportedUSStates.includes(orderDetails.usState.abbreviation)
    ) {
      setShowAddressModal({
        modalName: "stateNotSupported",
        modalData: {
          unsupportedState: orderDetails.usState.name,
        },
      });
      return;
    }

    setOrderDetailsData(
      {
        ...orderDetails,
        usState: orderDetails.usState.abbreviation,
      },
      checkoutSessionID
    );
    history.push(
      `/visits/${consultationType}/payment/payment-details?checkoutSessionID=${checkoutSessionID}`
    );
  };

  // form props
  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(orderDetailsFormValidationSchema),
  });

  return (
    <div className={`${classes.containerWithSpacing} payment-section`}>
      <div className={classes.checkoutProgressMobile}>
        <CheckoutProgress step={1} checkoutSessionID={checkoutSessionID} />
      </div>
      <GridContainer>
        <GridItem xs={12} md={4}>
          <SocialProof
            style={{ borderBottom: "1px solid #ddd", marginBottom: "24px" }}
            showtestimonials={isConsultationTypeInsomnia(
              consultationType
            ).toString()}
          />
          <ShippingRadios
            classes={classes}
            shipMethod={shipMethod}
            setShipMethod={setShipMethod}
            checkoutSessionID={checkoutSessionID}
            setTotalPrice={setTotalPrice}
          />
          {isConsultationTypeInsomnia(consultationType) && (
            <SleepSubscriptionSelectionSection
              consultationType={consultationType}
              sleepProductType={sleepProductType}
              setSleepVisitProductFromRadio={setSleepVisitProductFromRadio}
              classes={classes}
              checkoutSessionID={checkoutSessionID}
            />
          )}
          {isConsultationTypeInsomnia(consultationType) && (
            <SelectedSleepMeds />
          )}
          {isConsultationTypeInsomnia(consultationType) && <WhatHappensNext />}
        </GridItem>
        <GridItem xs={12} md={8}>
          <div className={classes.paymentForm}>
            <div className={classes.checkoutProgressDesktop}>
              <CheckoutProgress
                step={1}
                checkoutSessionID={checkoutSessionID}
              />
            </div>
            <form
              style={{ width: "100%" }}
              onSubmit={handleSubmit(async (data) => {
                console.log("[OrderDetails onSubmit] errors: ", errors);
                console.log(data);
              })}
            >
              <p className={classes.subtitle}>
                {isPharmacyPickupOrderType(getVisitProduct(checkoutSessionID))
                  ? "Billing Address"
                  : "Shipping Address"}
              </p>
              {!isPharmacyPickupOrderType(
                getVisitProduct(checkoutSessionID)
              ) && (
                <p
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginTop: "-2px",
                    marginBottom: "16px",
                  }}
                >
                  Your home, office, or somewhere you collect packages
                </p>
              )}

              <StateNotSupportedModal
                showModal={showAddressModal.modalName === "stateNotSupported"}
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                consultationType={consultationType}
                checkoutSessionID={checkoutSessionID}
              />

              <CompoundedStateNotSupportedModal
                showModal={
                  showAddressModal.modalName === "compoundedStateNotSupported"
                }
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                consultationType={consultationType}
              />

              <ConfirmAddressModal
                showModal={showAddressModal.modalName === "confirmAddress"}
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={(data) => {
                  if (data) {
                    validateAddress(googleAPIKey, {
                      ...getValues(),
                      ...data,
                    });
                  } else {
                    finishOrderDetailsSection(getValues());
                  }
                }}
              />

              <MissingAddressComponentsModal
                showModal={
                  showAddressModal.modalName === "missingAddressComponents"
                }
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={(data) => {
                  if (data) {
                    validateAddress(googleAPIKey, {
                      ...getValues(),
                      ...data,
                    });
                  } else {
                    finishOrderDetailsSection(getValues());
                  }
                }}
              />

              <CouldntVerifyAddressModal
                showModal={
                  showAddressModal.modalName === "couldntVerifyAddress"
                }
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={() => {
                  finishOrderDetailsSection(getValues());
                }}
              />

              <POBoxShippingNotSupportedModal
                showModal={
                  showAddressModal.modalName === "poBoxShippingNotSupported"
                }
                setShowModal={setShowAddressModal}
                message={showAddressModal.modalData?.message}
              />

              <ShippingInputs
                classes={classes}
                errors={errors}
                control={control}
                address1={address1}
                address2={address2}
                city={city}
                usState={usState}
                zip={zip}
                phone={phone}
                smsConsent={smsConsent}
              />

              {isConsultationTypeInsomnia(consultationType) ? (
                <SleepOrderCosts
                  classes={classes}
                  consultationType={consultationType}
                  shipMethod={shipMethod}
                  totalPrice={totalPrice}
                  checkoutSessionID={checkoutSessionID}
                />
              ) : (
                <OrderCosts
                  classes={classes}
                  consultationType={consultationType}
                  shipMethod={shipMethod}
                  totalPrice={totalPrice}
                  checkoutSessionID={checkoutSessionID}
                />
              )}

              <Button
                onClick={async (e) => {
                  setFormIsSubmitting(true);
                  e.preventDefault();
                  const valid = await trigger([
                    "address1",
                    "city",
                    "usState",
                    "zip",
                    "phone",
                  ]);
                  if (valid) {
                    const formValues = getValues();
                    const stateAbbr = formValues.usState?.abbreviation;
                    // check if state is supported
                    if (
                      isMailOrderType(getVisitProduct(checkoutSessionID)) &&
                      !supportedUSStates.includes(stateAbbr)
                    ) {
                      setFormIsSubmitting(false);
                      setShowAddressModal({
                        modalName: "stateNotSupported",
                        modalData: {
                          unsupportedState: formValues.usState.name,
                        },
                      });
                    } else {
                      const orderItems = getOrderItemsList(checkoutSessionID);
                      const hasFlavoredOrderItems = Object.values(
                        orderItems
                      ).some((orderItem) =>
                        isCompoundedOrderType(orderItem.productSlug)
                      );
                      if (
                        hasFlavoredOrderItems &&
                        !supportedUSStatesForCompoundedMeds.includes(stateAbbr)
                      ) {
                        setFormIsSubmitting(false);
                        setShowAddressModal({
                          modalName: "compoundedStateNotSupported",
                          modalData: {
                            unsupportedState: formValues.usState.name,
                          },
                        });
                        return;
                      }
                      await validateAddress(googleAPIKey, formValues);
                    }
                  } else {
                    setFormIsSubmitting(false);
                  }
                }}
                className={classes.confirmAndPayButton}
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  textTransform: "unset",
                  backgroundColor: kickAmaOrange,
                  color: kickNavy,
                }}
              >
                Next
                {formIsSubmitting ? (
                  <CircularProgress
                    size="large"
                    style={{
                      position: "relative",
                      left: "24px",
                      top: "1px",
                      color: kickNavy,
                    }}
                  />
                ) : null}
              </Button>
            </form>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default withStyles(consultationStyles)(OrderDetails);
